/**
 * Offline Authentication Hook
 * Provides easy access to offline authentication state and utilities
 * Requirements: 9.6, 9.7
 */

import { useEffect, useState } from 'react';
import { useOfflineAuthStore, offlineAuthHelpers } from '../store/offlineAuthStore';
import { useAuthStore } from '../store/authStore';
import { validateTokenOffline, getTokenValidationSummary } from '../utils/offlineTokenValidator';

interface OfflineAuthStatus {
  // Connection status
  isOnline: boolean;
  
  // Authentication status
  isAuthenticated: boolean; // Online authentication
  hasValidOfflineToken: boolean; // Offline token validity
  
  // User information
  userId: string | null;
  userEmail: string | null;
  
  // Token information
  tokenExpiresAt: Date | null;
  isTokenExpiringSoon: boolean;
  tokenTimeRemaining: number; // in seconds
  
  // Access permissions
  canAccessReadingHistory: boolean;
  canAccessOfflineFeatures: boolean;
  shouldShowOfflineWarning: boolean;
  
  // Status messages
  authStatusMessage: string;
  accessStatusMessage: string;
  
  // Actions
  refreshAuthStatus: () => void;
  checkTokenValidity: () => boolean;
}

/**
 * Hook for managing offline authentication state
 */
export const useOfflineAuth = (): OfflineAuthStatus => {
  const [tokenTimeRemaining, setTokenTimeRemaining] = useState(0);
  
  // Get auth stores
  const { isAuthenticated, user } = useAuthStore();
  const { 
    isOnline, 
    hasValidOfflineToken, 
    offlineUserId, 
    offlineUserEmail,
    tokenExpiresAt,
    refreshOfflineTokenStatus
  } = useOfflineAuthStore();

  // Update token time remaining
  useEffect(() => {
    const updateTimeRemaining = () => {
      if (tokenExpiresAt) {
        const remaining = Math.max(0, Math.floor((tokenExpiresAt.getTime() - Date.now()) / 1000));
        setTokenTimeRemaining(remaining);
      } else {
        setTokenTimeRemaining(0);
      }
    };

    updateTimeRemaining();
    
    // Update every 30 seconds
    const interval = setInterval(updateTimeRemaining, 30000);
    return () => clearInterval(interval);
  }, [tokenExpiresAt]);

  // Determine user information based on online/offline status
  const userId = isOnline ? user?.userId || null : offlineUserId;
  const userEmail = isOnline ? user?.email || null : offlineUserEmail;

  // Calculate token expiration status
  const { isExpiringSoon } = offlineAuthHelpers.getTokenExpirationInfo();

  // Determine access permissions
  const canAccessReadingHistory = isOnline 
    ? isAuthenticated 
    : hasValidOfflineToken;

  const canAccessOfflineFeatures = !isOnline && hasValidOfflineToken;
  
  const shouldShowOfflineWarning = !isOnline && (!hasValidOfflineToken || isExpiringSoon);

  // Generate status messages
  const authStatusMessage = isOnline
    ? (isAuthenticated ? 'Authenticated online' : 'Not authenticated')
    : (hasValidOfflineToken ? 'Valid offline token' : 'No valid offline token');

  const accessStatusMessage = offlineAuthHelpers.getOfflineAuthStatusMessage();

  // Actions
  const refreshAuthStatus = () => {
    if (isOnline) {
      // Trigger online auth check (handled by main auth store)
      useAuthStore.getState().checkAuth();
    } else {
      // Refresh offline token status
      refreshOfflineTokenStatus();
    }
  };

  const checkTokenValidity = (): boolean => {
    const token = localStorage.getItem('jwt_token');
    const validation = validateTokenOffline(token);
    return validation.isValid;
  };

  return {
    // Connection status
    isOnline,
    
    // Authentication status
    isAuthenticated: isOnline ? isAuthenticated : hasValidOfflineToken,
    hasValidOfflineToken,
    
    // User information
    userId,
    userEmail,
    
    // Token information
    tokenExpiresAt,
    isTokenExpiringSoon: isExpiringSoon,
    tokenTimeRemaining,
    
    // Access permissions
    canAccessReadingHistory,
    canAccessOfflineFeatures,
    shouldShowOfflineWarning,
    
    // Status messages
    authStatusMessage,
    accessStatusMessage,
    
    // Actions
    refreshAuthStatus,
    checkTokenValidity
  };
};

/**
 * Hook for checking if a specific feature is available offline
 */
export const useOfflineFeatureAccess = (featureName: string): {
  isAvailable: boolean;
  reason: string;
  canUseFeature: boolean;
} => {
  const { isOnline, hasValidOfflineToken, canAccessOfflineFeatures } = useOfflineAuth();

  // Define which features are available offline
  const offlineFeatures = {
    'reading-history': true,
    'cached-readings': true,
    'theme-settings': true,
    'profile-view': true,
    'shop-browse': false, // Requires API
    'new-reading': false, // Requires AI API
    'profile-edit': false, // Requires API
    'purchase': false, // Requires API
  };

  const isFeatureOfflineCapable = offlineFeatures[featureName as keyof typeof offlineFeatures] ?? false;

  if (isOnline) {
    return {
      isAvailable: true,
      reason: 'Online - all features available',
      canUseFeature: true
    };
  }

  if (!hasValidOfflineToken) {
    return {
      isAvailable: false,
      reason: 'Offline access requires valid authentication',
      canUseFeature: false
    };
  }

  if (!isFeatureOfflineCapable) {
    return {
      isAvailable: false,
      reason: 'This feature requires internet connection',
      canUseFeature: false
    };
  }

  return {
    isAvailable: true,
    reason: 'Available offline with cached data',
    canUseFeature: canAccessOfflineFeatures
  };
};

/**
 * Hook for monitoring token expiration and providing warnings
 */
export const useTokenExpirationMonitor = (warningThresholdMinutes: number = 5): {
  isExpiringSoon: boolean;
  timeUntilExpiration: string;
  shouldShowWarning: boolean;
  expirationDate: Date | null;
} => {
  const { tokenExpiresAt, tokenTimeRemaining, isOnline } = useOfflineAuth();
  
  const warningThresholdSeconds = warningThresholdMinutes * 60;
  const isExpiringSoon = tokenTimeRemaining > 0 && tokenTimeRemaining <= warningThresholdSeconds;
  const shouldShowWarning = !isOnline && isExpiringSoon;

  // Format time until expiration
  const formatTimeRemaining = (seconds: number): string => {
    if (seconds <= 0) return 'Expired';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return {
    isExpiringSoon,
    timeUntilExpiration: formatTimeRemaining(tokenTimeRemaining),
    shouldShowWarning,
    expirationDate: tokenExpiresAt
  };
};

/**
 * Hook for getting detailed token validation information (for debugging)
 */
export const useTokenValidationInfo = () => {
  const [validationInfo, setValidationInfo] = useState(() => {
    const token = localStorage.getItem('jwt_token');
    return getTokenValidationSummary(token);
  });

  useEffect(() => {
    const updateValidationInfo = () => {
      const token = localStorage.getItem('jwt_token');
      setValidationInfo(getTokenValidationSummary(token));
    };

    // Update immediately
    updateValidationInfo();

    // Update every minute
    const interval = setInterval(updateValidationInfo, 60000);
    return () => clearInterval(interval);
  }, []);

  return validationInfo;
};